import React, { useEffect, useMemo, useState } from "react";
import { DotsSixVertical, GearSix, ArrowsClockwise, ArrowDown, ArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";

/* Modular admin layout
 * ------------------------------------------------------------------
 * `sections` is an array of { key, tier, render }. The user can:
 *  - Drag handles to reorder sections (HTML5 DnD)
 *  - Apply a "preset" (analytics-first / standard / analytics-last)
 *  - Persisted to localStorage so the order survives reloads
 *
 * Tier filtering is applied here: sections whose tier isn't unlocked by the
 * current admin mode are skipped entirely.
 */

const STORAGE_KEY = "ush_admin_layout_v1";

export const PRESET_STANDARD = "standard";
export const PRESET_ANALYTICS_FIRST = "analytics-first";
export const PRESET_ANALYTICS_LAST = "analytics-last";

const ANALYTICS_KEYS = ["analytics", "ratings"];

function applyPreset(allKeys, preset) {
  if (preset === PRESET_ANALYTICS_FIRST) {
    const first = allKeys.filter((k) => ANALYTICS_KEYS.includes(k));
    const rest = allKeys.filter((k) => !ANALYTICS_KEYS.includes(k));
    return [...first, ...rest];
  }
  if (preset === PRESET_ANALYTICS_LAST) {
    const last = allKeys.filter((k) => ANALYTICS_KEYS.includes(k));
    const rest = allKeys.filter((k) => !ANALYTICS_KEYS.includes(k));
    return [...rest, ...last];
  }
  return allKeys;
}

function loadStoredOrder() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.order)) return null;
    return parsed;
  } catch (_err) { void _err; /* corrupt order JSON — fall back to default layout */ return null; }
}

function persistOrder(state) {
  try {
    if (state) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (_err) { void _err; /* localStorage disabled — layout reverts to default on reload */ }
}

export default function ModularLayout({ sections, isUnlocked }) {
  // Default order = the order defined by the caller.
  const defaultOrder = useMemo(() => sections.map((s) => s.key), [sections]);
  const [order, setOrder] = useState(() => {
    const stored = loadStoredOrder();
    if (!stored) return defaultOrder;
    // Drop any keys that no longer exist; append any new keys at the bottom.
    const known = new Set(defaultOrder);
    const filtered = stored.order.filter((k) => known.has(k));
    const missing = defaultOrder.filter((k) => !filtered.includes(k));
    return [...filtered, ...missing];
  });
  useEffect(() => { persistOrder({ order }); }, [order]);

  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  // Build a lookup of section configs.
  const byKey = useMemo(() => Object.fromEntries(sections.map((s) => [s.key, s])), [sections]);

  // Visible list: filter out locked tiers + map to section configs.
  const visibleOrder = useMemo(() => order.filter((k) => {
    const s = byKey[k];
    return s && isUnlocked(s.tier);
  }), [order, byKey, isUnlocked]);

  const move = (from, to) => {
    if (from === to) return;
    const next = [...order];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setOrder(next);
  };

  const moveKey = (key, delta) => {
    const idx = order.indexOf(key);
    if (idx < 0) return;
    const target = idx + delta;
    if (target < 0 || target >= order.length) return;
    move(idx, target);
  };

  const applyPresetClick = (preset) => {
    setOrder(applyPreset(defaultOrder, preset));
    const name = preset === PRESET_ANALYTICS_FIRST ? "Analytics first"
      : preset === PRESET_ANALYTICS_LAST ? "Analytics last" : "Standard";
    toast.success(`Preset: ${name}`);
  };

  const reset = () => {
    setOrder(defaultOrder);
    toast.success("Layout reset");
  };

  return (
    <>
      <LayoutToolbar onPreset={applyPresetClick} onReset={reset} />
      {visibleOrder.map((key) => {
        const section = byKey[key];
        if (!section) return null;
        const isDragging = dragKey === key;
        const isOver = overKey === key && dragKey !== key;
        return (
          <div
            key={key}
            className={`mt-12 pt-10 border-t border-ink-600 transition-all ${isDragging ? "opacity-50" : ""} ${isOver ? "ring-1 ring-neon-cyan/40 rounded-xl" : ""}`}
            data-testid={`layout-section-${key}`}
            onDragOver={(e) => { e.preventDefault(); if (dragKey && dragKey !== key) setOverKey(key); }}
            onDragLeave={() => { if (overKey === key) setOverKey(null); }}
            onDrop={(e) => {
              e.preventDefault();
              const from = order.indexOf(dragKey ?? e.dataTransfer.getData("text/plain"));
              const to = order.indexOf(key);
              setDragKey(null); setOverKey(null);
              if (from >= 0 && to >= 0) move(from, to);
            }}
          >
            <div className="flex items-center gap-2 mb-3 -mt-2">
              <button
                draggable
                onDragStart={(e) => { setDragKey(key); try { e.dataTransfer.setData("text/plain", key); } catch (_err) { void _err; } e.dataTransfer.effectAllowed = "move"; }}
                onDragEnd={() => { setDragKey(null); setOverKey(null); }}
                className="text-zinc-600 hover:text-neon-cyan cursor-grab active:cursor-grabbing p-1 rounded hover:bg-ink-800"
                title="Drag to reorder section"
                data-testid={`layout-drag-${key}`}
                aria-label={`Drag handle for ${section.label}`}
              >
                <DotsSixVertical size={14} weight="bold" />
              </button>
              <button onClick={() => moveKey(key, -1)} className="text-zinc-600 hover:text-neon-cyan p-1 rounded hover:bg-ink-800" title="Move up" data-testid={`layout-up-${key}`}>
                <ArrowUp size={12} weight="bold" />
              </button>
              <button onClick={() => moveKey(key, 1)} className="text-zinc-600 hover:text-neon-cyan p-1 rounded hover:bg-ink-800" title="Move down" data-testid={`layout-down-${key}`}>
                <ArrowDown size={12} weight="bold" />
              </button>
              <span className="text-[10px] text-zinc-600 font-mono uppercase tracking-[0.2em]">{section.label}</span>
            </div>
            {section.render()}
          </div>
        );
      })}
    </>
  );
}

function LayoutToolbar({ onPreset, onReset }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-8 flex items-center justify-end relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink-600 text-zinc-400 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs font-mono uppercase tracking-widest"
        data-testid="layout-toolbar-toggle"
      >
        <GearSix size={14} weight="bold" /> Layout
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 z-20 rounded-lg border border-ink-600 bg-ink-900/95 backdrop-blur-md shadow-xl p-2 w-56 text-xs" data-testid="layout-toolbar-menu">
          <button onClick={() => { onPreset(PRESET_STANDARD); setOpen(false); }} className="w-full text-left px-2 py-1.5 rounded text-zinc-300 hover:bg-ink-800 hover:text-white" data-testid="layout-preset-standard">Preset · Standard</button>
          <button onClick={() => { onPreset(PRESET_ANALYTICS_FIRST); setOpen(false); }} className="w-full text-left px-2 py-1.5 rounded text-zinc-300 hover:bg-ink-800 hover:text-white" data-testid="layout-preset-analytics-first">Preset · Analytics first</button>
          <button onClick={() => { onPreset(PRESET_ANALYTICS_LAST); setOpen(false); }} className="w-full text-left px-2 py-1.5 rounded text-zinc-300 hover:bg-ink-800 hover:text-white" data-testid="layout-preset-analytics-last">Preset · Analytics last</button>
          <div className="border-t border-ink-700 my-1" />
          <button onClick={() => { onReset(); setOpen(false); }} className="w-full text-left px-2 py-1.5 rounded text-zinc-400 hover:bg-ink-800 hover:text-neon-magenta inline-flex items-center gap-1.5" data-testid="layout-reset">
            <ArrowsClockwise size={12} weight="bold" /> Reset to default
          </button>
        </div>
      )}
    </div>
  );
}
